import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { AppContext, InvoiceProductInfo } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { InvoiceStatus } from '../../../enums/InvoiceStatusEnums';
import { Types } from '../../../enums/TypeEnums';
import { useProductsLazyQuery, IProduct } from '../../../generated/graphql';
import { canUserEdit } from '../../../utils/rightUtils';
import { normalizePercentInput } from '../../../utils/mathUtils';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import InputComponent from '../../Common/Fields/InputComponent';
import { Colors } from '../../../utils/theme';
import { defaultProductInfo } from '../../../models/defaults';
import moment from 'moment';

interface IProps {
  productInfoInput?: InvoiceProductInfo;
  setProductInfo?: (productInfo: InvoiceProductInfo) => void;
}

const ProductAndPricingStep: React.FunctionComponent<IProps> = ({
  productInfoInput,
  setProductInfo
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [productOptions, setProductOptions] = useState([]);
  const [getProducts, productsData] = useProductsLazyQuery();
  const [product, setProduct] = useState<IProduct | null>(null);
  const [discountPercent, setDiscountPercent] = useState<number | null>(null);
  const [vatPercent, setVatPercent] = useState<number | null>(null);
  const [price, setPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [vat, setVat] = useState(0);
  const [total, setTotal] = useState(0);
  const [notes, setNotes] = useState<string | null>('');
  const [poNumber, setPoNumber] = useState<string | null>('');
  const [startDate, setStartDate] = useState<string | null>('');
  const canEdit = canUserEdit(state.user, Types.Invoice);
  const editable =
    canEdit &&
    (state.selectedInvoice?.status === InvoiceStatus.New ||
      state.selectedInvoice?.status === InvoiceStatus.Pending);
  useEffect(() => {
    setVat(vatPercent ? (price - discount) * vatPercent : 0);
    if (editable) {
      getProducts();
    }
    const productInfo =
      productInfoInput ?? state.selectedInvoice?.productInfo ?? defaultProductInfo;
    setProduct(productInfo?.product);
    setPrice(Number(productInfo?.product?.price));
    setDiscountPercent(Number(productInfo.discountPercent));
    setDiscount(Number(productInfo.discount));
    setVatPercent(Number(productInfo.vatPercent));
    setVat(Number(productInfo.vat));
    setTotal(Number(productInfo.total));
    setNotes(state.selectedInvoice?.notes ?? '');
    setPoNumber(state.selectedInvoice?.po_nr ?? '');
    setStartDate(state.selectedInvoice?.invoice_full?.remarks ?? '');
  }, []);
  useEffect(() => {
    if (productsData.data && productsData.data.result) {
      setProductOptions(productsData.data.result as never[]);
    }
  }, [productsData]);

  const calculateTotal = (price: number, discount: number, vat: number) => {
    const newTotal = numberToFixed(price === 0 ? 0 : price - discount + vat / 100);
    setTotal(newTotal);
    return newTotal;
  };

  const handleProductChanged = (_: unknown, newValue: React.SetStateAction<IProduct | null>) => {
    const productInfo = getProductInfo(newValue as IProduct | null);
    if (newValue) {
      dispatch({
        type: Actions.ResetInvoiceErrors
      });
      dispatch({
        type: Actions.SetInvoiceProductInfo,
        payload: {
          product: productInfo.product,
          discount: productInfo.discount,
          discountPercent: discountPercent ?? 0,
          total: productInfo.total,
          vat: productInfo.vat,
          vatPercent: productInfo.vatPercent ?? 0
        }
      });
    } else {
      dispatch({
        type: Actions.ResetInvoiceProductInfo
      });
    }
    setDiscount(productInfo.discount);
    setVat(productInfo.vat);
    setVatPercent(productInfo.vatPercent);
    setPrice(productInfo.product?.price ?? 0);
    setProduct(newValue);
    setDiscountPercent(productInfo.discountPercent);
    setTotal(productInfo.total);

    if (setProductInfo) {
      setProductInfo(productInfo);
    }
  };

  const getProductInfo = (product: IProduct | null) => {
    const result = { ...defaultProductInfo };
    if (product) {
      result.product = product;
      const newDiscount = numberToFixed(
        discountPercent && discountPercent > 0 ? (product.price * discountPercent) / 100 : 0
      );
      const newVatPercent = numberToFixed(
        product.vat && product.vat > 0 ? ((product.price - newDiscount) * product.vat) / 100 : 0
      );
      const newVat = newVatPercent * 100;
      const newTotal = calculateTotal(product.price, result.discount, newVat);
      result.discount = newDiscount;
      result.vat = newVatPercent ?? 0;
      result.vatPercent = product.vat ?? newVatPercent ?? 0;
      result.total = newTotal;
    }
    return result;
  };

  const handleVatPercentChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    const valueAsNumber = Number(value);
    const newVatPercent = normalizePercentInput(valueAsNumber);
    let newVat = 0;
    if (value) {
      newVat = numberToFixed(((price - discount) * newVatPercent) / 100);
    }
    setVat(newVat);
    setVatPercent(newVatPercent);
    const newTotal = calculateTotal(price, discount, newVat * 100);
    dispatch({
      type: Actions.SetInvoiceProductInfo,
      payload: {
        ...(state.selectedInvoice?.productInfo as InvoiceProductInfo),
        total: newTotal,
        vat: newVat,
        vatPercent: newVatPercent
      }
    });
    if (productInfoInput && setProductInfo) {
      setProductInfo({
        ...productInfoInput,
        vat: newVat,
        vatPercent: newVatPercent,
        total: newTotal
      });
    }
  };

  const handleDiscountPercentChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    const valueAsNumber = Number(value);
    const newDiscountPercent = normalizePercentInput(valueAsNumber);
    const newDiscount = numberToFixed(
      newDiscountPercent === 0 ? newDiscountPercent : (price * newDiscountPercent) / 100
    );
    let newVat = 0;
    if (vatPercent) {
      newVat = numberToFixed(((price - newDiscount) * vatPercent) / 100);
      setVat(newVat);
    }
    setDiscountPercent(newDiscountPercent);
    setDiscount(newDiscount);
    const newTotal = calculateTotal(price, newDiscount, newVat * 100);

    dispatch({
      type: Actions.SetInvoiceProductInfo,
      payload: {
        ...(state.selectedInvoice?.productInfo as InvoiceProductInfo),
        vat: newVat,
        discount: newDiscount,
        discountPercent: newDiscountPercent,
        total: newTotal
      }
    });
    if (productInfoInput && setProductInfo) {
      setProductInfo({
        ...productInfoInput,
        discountPercent: newDiscountPercent,
        discount: newDiscount,
        total: newTotal,
        vat: newVat
      });
    }
  };

  const numberToFixed = (input: number, decimalPlaces?: number): number => {
    const places = decimalPlaces ?? 2;
    return Number(input.toFixed(places));
  };

  return (
    <Box
      sx={{
        pt: '153px',
        px: '28.25%',
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: '30px'
      }}
    >
      <AutocompleteComponent
        id="product-name"
        data-testid="invoice-product-name"
        label="Product"
        options={productOptions}
        disabled={!editable}
        values={product}
        onChange={handleProductChanged}
        getOptionLabel={(option: IProduct) => option?.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        errorMsg={state.selectedInvoice?.errors?.product}
        additionalStyle={{ gridColumn: 'span 3' }}
      />
      <InputComponent id="invoice-price" label="Price" value={price} disabled type="number" />
      <InputComponent
        id="invoice-discount-percent"
        label="Discount %"
        data-testid="invoice-discount-percent"
        type="number"
        onChange={handleDiscountPercentChange}
        additionalStyle={{ color: !editable || !product ? Colors.gray : Colors.main }}
        value={discountPercent || ''}
        disabled={!editable || !product}
      />
      <InputComponent
        id="invoice-vat-percent"
        onChange={handleVatPercentChange}
        label="VAT %"
        data-testid="invoice-vat-percent"
        type="number"
        additionalStyle={{ color: !editable || !product ? Colors.gray : Colors.main }}
        value={vatPercent || ''}
        disabled={!editable || !product}
      />
      <InputComponent
        id="invoice-discount"
        data-testid="invoice-discount-label"
        label="Discount"
        disabled={true}
        value={discount}
        type="number"
      />
      <InputComponent
        id="invoice-vat"
        data-testid="invoice-vat-label"
        label="VAT"
        value={vat}
        disabled={true}
        type="number"
      />
      <InputComponent
        id="invoice-po"
        data-testid="invoice-product-po"
        value={poNumber || ''}
        onChange={(e) => {
          setPoNumber(e.currentTarget.value);
          dispatch({
            type: Actions.SetInvoicePoNumber,
            payload: { text: e.currentTarget.value }
          });
        }}
        label="PO"
        disabled={!editable && state.selectedInvoice?.status !== InvoiceStatus.Confirmed}
        additionalStyle={{ gridColumn: 'span 3' }}
      />
      <InputComponent
        id="invoice-total"
        value={total}
        data-testid="invoice-total-input-label"
        label="Total"
        disabled={true}
        additionalStyle={{ font: 'bold' }}
        type="number"
      />
      <InputComponent
        id="membership-period-start-date"
        label="Start Date"
        value={startDate || ''}
        onChange={(e) => {
          setStartDate(e.currentTarget.value);
          dispatch({
            type: Actions.SetMembershipStartDate,
            payload: { text: e.currentTarget.value }
          });
        }}
        disabled={!editable && state.selectedInvoice?.status !== InvoiceStatus.Confirmed}
        additionalStyle={{ gridColumn: 'span 4' }}
      />
      <InputComponent
        id="invoice-notes"
        value={notes}
        onChange={(e) => {
          setNotes(e.currentTarget.value);
          dispatch({ type: Actions.SetInvoiceNotes, payload: { text: e.currentTarget.value } });
        }}
        label="Notes"
        additionalStyle={{ gridColumn: 'span 4' }}
        disabled={!editable}
      />
    </Box>
  );
};

export default ProductAndPricingStep;
